import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, User, MapPin, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReviewList from "@/components/ReviewList";
import SellerRating from "@/components/SellerRating";
import { supabase } from "@/integrations/supabase/client";

const PublicProfile = () => {
  const { userId } = useParams();
  const [profile, setProfile] = useState<any>(null);
  const [totalStock, setTotalStock] = useState(0);
  const [listingCount, setListingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;

    const fetchData = async () => {
      const { data: p } = await supabase
        .from("seller_profiles_public")
        .select("*")
        .eq("user_id", userId)
        .single();
      setProfile(p);

      const { data: listings } = await supabase
        .from("listings")
        .select("stock, volume")
        .eq("seller_id", userId)
        .eq("status", "active");

      if (listings) {
        setListingCount(listings.length);
        setTotalStock(listings.reduce((sum, l) => sum + (l.stock ?? l.volume ?? 0), 0));
      }
      setLoading(false);
    };

    fetchData();
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center text-muted-foreground">Chargement...</div>
        <Footer />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="font-display text-2xl font-bold mb-2">Profil introuvable</p>
            <Button variant="outline" asChild>
              <Link to="/annonces"><ArrowLeft className="h-4 w-4 mr-2" /> Retour</Link>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const displayName = profile.display_name || "Utilisateur";

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="container py-8 flex-1 max-w-2xl">
        <Button variant="ghost" size="sm" asChild className="mb-6 gap-2">
          <Link to="/annonces"><ArrowLeft className="h-4 w-4" /> Retour</Link>
        </Button>

        <div className="bg-card rounded-lg border p-8 text-center mb-6">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            {profile.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="w-20 h-20 rounded-full object-cover" />
            ) : (
              <User className="h-10 w-10 text-primary" />
            )}
          </div>
          <h1 className="font-display text-2xl font-bold mb-1">{displayName}</h1>
          {profile.location && (
            <p className="text-sm text-muted-foreground flex items-center justify-center gap-1">
              <MapPin className="h-4 w-4" /> {profile.location}
            </p>
          )}
          <div className="mt-3">
            <SellerRating rating={profile.admin_rating ?? 0} size="md" />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-card rounded-lg border p-4 text-center">
            <Package className="h-5 w-5 text-primary mx-auto mb-1" />
            <p className="font-display text-xl font-bold">{totalStock}</p>
            <p className="text-xs text-muted-foreground">stères disponibles</p>
          </div>
          <div className="bg-card rounded-lg border p-4 text-center">
            <Package className="h-5 w-5 text-primary mx-auto mb-1" />
            <p className="font-display text-xl font-bold">{listingCount}</p>
            <p className="text-xs text-muted-foreground">annonces actives</p>
          </div>
        </div>

        {/* Reviews */}
        <div className="bg-card rounded-lg border p-6">
          <ReviewList sellerId={userId!} />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PublicProfile;
